import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Card,
  CardContent,
  CardMedia,
  MobileStepper,
  Paper,
  useTheme,
} from "@mui/material";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";

import heroImg from "../../assets/hero.jpg";

// Carousel images for rental properties (Unsplash, curated for realistic, nice rentals)
const rentalImages = [
  {
    src: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80",
    alt: "Cozy modern apartment",
  },
  {
    src: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1200&q=80",
    alt: "Bright loft with natural light",
  },
  {
    src: "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&w=1200&q=80",
    alt: "Welcoming rental apartment",
  },
];

const carouselSteps = [
  {
    label: "Elevated Property Care",
    description:
      "We return your property in better condition than received, with weekly professional cleaning and meticulous maintenance.",
  },
  {
    label: "Selective Guest Screening",
    description:
      "Our guests are primarily traveling nurses, doctors, and business professionals—no parties, no smoking, and rarely small children.",
  },
  {
    label: "Guaranteed Income",
    description:
      "Enjoy steady, reliable income every month, even if your property isn’t leased, with no financial risk.",
  },
];

const features = [
  {
    title: "Impeccable Standards",
    description:
      "Weekly professional cleaning and rigorous upkeep ensure your property remains pristine.",
    icon: "✨",
  },
  {
    title: "Premium Guest Profile",
    description:
      "We cater to professionals and discerning travelers, ensuring respectful use of your property.",
    icon: "👩‍⚕️",
  },
  {
    title: "Hassle-Free Partnership",
    description:
      "We manage every detail—guest communication, bookings, and cleaning coordination—for your peace of mind.",
    icon: "🤝",
  },
];

const AirbnbPartnershipPage = () => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [rentalStep, setRentalStep] = useState(0);

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);
  const handleRentalNext = () => setRentalStep((prev) => prev + 1);
  const handleRentalBack = () => setRentalStep((prev) => prev - 1);

  return (
    <Box sx={{ bgcolor: "#f9fafb", color: "#1a1a1a", minHeight: "100vh" }}>
      {/* Hero Section */}
      <Box
        sx={{
          width: "100%",
          height: { xs: "70vh", sm: "80vh", md: "90vh" },
          background: `url(${heroImg}) center/cover no-repeat`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <Box
          sx={{
            bgcolor: "rgba(255, 255, 255, 0.3)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            px: 4,
            py: 3,
            borderRadius: 2,
            textAlign: "center",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              color: "#1a1a1a",
              fontSize: { xs: "2rem", md: "3rem" },
            }}
          >
            Premier Property Partnership
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ color: "#1a1a1a", mt: 1, fontWeight: 500 }}
          >
            Elevate your property with our expert management
          </Typography>
        </Box>
      </Box>

      {/* Intro Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" fontWeight={600} gutterBottom align="center">
          Transform Your Property into a Premium Rental
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 4, maxWidth: 800, mx: "auto", textAlign: "center" }}
        >
          Partner with us to unlock your property’s potential through our
          expertly managed rental program. We ensure your property is impeccably
          maintained, leased to discerning professionals, and generates reliable
          income—hassle-free.
        </Typography>
      </Container>

      {/* Carousel Section */}
      <Container maxWidth="md" sx={{ mb: 8 }}>
        <Paper
          elevation={3}
          sx={{
            bgcolor: "#fff",
            color: "#1a1a1a",
            p: 4,
            borderRadius: 4,
            mb: 3,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          <Typography variant="h5" fontWeight={600} gutterBottom>
            {carouselSteps[activeStep].label}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {carouselSteps[activeStep].description}
          </Typography>
        </Paper>
        <MobileStepper
          steps={carouselSteps.length}
          position="static"
          activeStep={activeStep}
          nextButton={
            <Button
              size="small"
              onClick={handleNext}
              disabled={activeStep === carouselSteps.length - 1}
              sx={{ color: theme.palette.primary.main, fontWeight: 600 }}
            >
              Next
              <KeyboardArrowRight />
            </Button>
          }
          backButton={
            <Button
              size="small"
              onClick={handleBack}
              disabled={activeStep === 0}
              sx={{ color: theme.palette.primary.main, fontWeight: 600 }}
            >
              <KeyboardArrowLeft />
              Back
            </Button>
          }
          sx={{ bgcolor: "transparent", justifyContent: "center" }}
        />
      </Container>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={4}
          alignItems="stretch"
        >
          {features.map((feature) => (
            <Card
              key={feature.title}
              sx={{
                flex: 1,
                bgcolor: "#fff",
                color: "#1a1a1a",
                borderRadius: 4,
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: 4,
                minHeight: 240,
                transition: "transform 0.3s",
                "&:hover": { transform: "translateY(-4px)" },
              }}
            >
              <Typography variant="h2" sx={{ mb: 2, fontSize: "2.5rem" }}>
                {feature.icon}
              </Typography>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Container>

      {/* Rental Properties Carousel */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Card
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            bgcolor: "#f9fafb",
          }}
        >
          <CardMedia
            component="img"
            height="300"
            image={rentalImages[rentalStep].src}
            alt={rentalImages[rentalStep].alt}
            sx={{ objectFit: "cover", width: "100%" }}
          />
        </Card>
        <MobileStepper
          steps={rentalImages.length}
          position="static"
          activeStep={rentalStep}
          nextButton={
            <Button
              size="small"
              onClick={handleRentalNext}
              disabled={rentalStep === rentalImages.length - 1}
              sx={{ color: theme.palette.primary.main, fontWeight: 600 }}
            >
              Next
              <KeyboardArrowRight />
            </Button>
          }
          backButton={
            <Button
              size="small"
              onClick={handleRentalBack}
              disabled={rentalStep === 0}
              sx={{ color: theme.palette.primary.main, fontWeight: 600 }}
            >
              <KeyboardArrowLeft />
              Back
            </Button>
          }
          sx={{ bgcolor: "transparent", justifyContent: "center", mt: 2 }}
        />
      </Container>

      {/* Call to Action */}
      <Container maxWidth="md" sx={{ pb: 10 }}>
        <Paper
          elevation={3}
          sx={{
            bgcolor: "rgba(255, 255, 255, 0.3)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            p: 5,
            borderRadius: 4,
            textAlign: "center",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            color: "#1a1a1a",
          }}
        >
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Partner with Confidence
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, maxWidth: 600, mx: "auto", color: "text.secondary" }}
          >
            Discover how our premier rental program can elevate your property
            while ensuring peace of mind and steady income. Contact us to see if
            your property qualifies.
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            href="mailto:contact@globalga.co?subject=Premier%20Rental%20Partnership%20Inquiry"
            sx={{
              px: 6,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              bgcolor: theme.palette.primary.main,
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              transition: "transform 0.3s, box-shadow 0.3s",
              "&:hover": {
                bgcolor: theme.palette.primary.dark,
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            Get Started Today
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default AirbnbPartnershipPage;
